'use client'

import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { motion } from 'framer-motion'
import { 
  Shield, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Loader,
  Settings,
  RefreshCw
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'

interface RedditConnectionRequiredProps {
  children: React.ReactNode
  feature: string
  description: string
}

interface RedditAccount {
  id: string
  user_id: string
  project_id: string
  reddit_username: string
  access_token: string
  refresh_token: string
  expires_at: string
  created_at: string
  updated_at: string
}

// Helper function to check if token is expired
const isTokenExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return true
  return new Date(expiresAt) <= new Date()
}

export default function RedditConnectionRequired({ 
  children, 
  feature, 
  description 
}: RedditConnectionRequiredProps) {
  const { id: projectId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isConnecting, setIsConnecting] = useState(false)

  // Check if Reddit account is connected for this project
  const { data: redditAccount, isLoading } = useQuery({
    queryKey: ['reddit-account', projectId],
    queryFn: async (): Promise<RedditAccount | null> => {
      if (!projectId || !user?.id) return null

      const { data, error } = await supabase
        .from('reddit_accounts' as any)
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle()

      if (error) {
        console.error('Error fetching Reddit account:', error)
        return null
      }

      // Check if data exists and has the required properties
      if (!data || typeof data !== 'object') {
        return null
      }
      
      // Type guard to check if it's a valid Reddit account
      const isValidRedditAccount = (obj: any): obj is RedditAccount => {
        return obj &&
               typeof obj === 'object' &&
               typeof obj.access_token === 'string' &&
               typeof obj.expires_at === 'string' &&
               typeof obj.reddit_username === 'string'
      }
      
      if (isValidRedditAccount(data)) {
        return data
      }

      return null
    },
    enabled: !!projectId && !!user?.id,
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  })

  // Check if we have a valid, non-expired connection
  const hasValidConnection = redditAccount && 
                           redditAccount.access_token && 
                           !isTokenExpired(redditAccount.expires_at)

  const handleConnectReddit = async () => {
    if (!projectId) return
    
    try {
      setIsConnecting(true)
      
      // Check if Reddit environment variables are configured
      const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID
      
      console.log('Reddit OAuth Debug Info:', {
        clientId: clientId || 'NOT_SET',
        redirectUri: `${window.location.origin}/auth/reddit/callback`,
        projectId: projectId,
        currentUrl: window.location.href,
        isReauth: !!redditAccount // Log if this is a reauthentication
      })
      
      if (!clientId || clientId === 'your_reddit_client_id' || clientId === 'your_reddit_client_id_here') {
        console.error('Reddit client ID not configured. Current value:', clientId)
        toast({
          title: "Configuration Required",
          description: "Reddit client ID is not set. Check the REDDIT_SETUP.md file for instructions.",
          variant: "destructive"
        })
        
        // Navigate to settings where they can see the configuration instructions
        navigate(`/projects/${projectId}/reddit/settings`)
        return
      }
      
      // Generate OAuth URL
      const state = crypto.randomUUID()
      const redirectUri = `${window.location.origin}/auth/reddit/callback`
      
      const authUrl = `https://www.reddit.com/api/v1/authorize?` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `state=${state}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `duration=permanent&` +
        `scope=identity,submit,read,mysubreddits`
      
      console.log('Redirecting to Reddit OAuth:', authUrl)
      
      // Store state and projectId for callback verification
      localStorage.setItem('reddit_oauth_state', state)
      localStorage.setItem('reddit_oauth_project_id', projectId)
      localStorage.setItem('reddit_oauth_return_url', window.location.href)
      
      // Redirect to Reddit OAuth
      window.location.href = authUrl
      
    } catch (error) {
      console.error('Error initiating Reddit OAuth:', error)
      toast({
        title: "Connection Failed",
        description: "Failed to initiate Reddit connection. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsConnecting(false)
    }
  }

  const handleGoToSettings = () => {
    navigate(`/projects/${projectId}/reddit/settings`)
  }

  // Show loading state while checking connection
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader className="h-6 w-6 text-blue-600 animate-spin" />
          <span className="text-zinc-600">Checking Reddit connection...</span>
        </div>
      </div>
    )
  }

  // Show connection required screen if not connected OR if token is expired
  if (!hasValidConnection) {
    const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID
    const isConfigured = clientId && clientId !== 'your_reddit_client_id' && clientId !== 'your_reddit_client_id_here'
    const isReauth = redditAccount && isTokenExpired(redditAccount.expires_at)

    return (
      <div className="container py-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-0 shadow-lg">
            <CardHeader className="text-center bg-gradient-to-r from-blue-50 to-blue-50 border-b border-blue-200">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary rounded-2xl blur-lg opacity-30"></div>
                  <div className="relative bg-primary p-3 rounded-2xl">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-zinc-900">
                {isReauth ? 'Reddit Connection Expired' : 'Reddit Connection Required'}
              </CardTitle>
              <p className="text-zinc-600 mt-2 max-w-md mx-auto">
                {isReauth ? (
                  <>Your Reddit connection has expired. Please reconnect to continue using <strong>{feature}</strong>.</>
                ) : (
                  <>To access <strong>{feature}</strong>, you need to connect your Reddit account to this project.</>
                )}
              </p>
            </CardHeader>

            <CardContent className="p-8">
              <div className="space-y-6">
                {!isConfigured && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <strong>Setup Required:</strong> Reddit integration needs to be configured. 
                      You'll need to create a Reddit app and set up the environment variables.
                    </AlertDescription>
                  </Alert>
                )}

                {isReauth && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Token Expired:</strong> Your Reddit authentication has expired. 
                      Reconnect to restore full functionality.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Connect Button - Primary Action */}
                <div className="flex justify-center pt-2">
                  {isConfigured ? (
                    <Button 
                      onClick={handleConnectReddit}
                      disabled={isConnecting}
                      size="lg"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-8 py-3 text-lg"
                    >
                      {isConnecting ? (
                        <>
                          <RefreshCw className="h-5 w-5 mr-3 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          {isReauth ? 'Reconnect Reddit Account' : 'Connect Reddit Account'}
                          <ArrowRight className="h-5 w-5 ml-3" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleGoToSettings}
                      size="lg"
                      variant="outline"
                      className="border-blue-600 text-blue-600 hover:bg-blue-50 font-medium px-8 py-3 text-lg"
                    >
                      <Settings className="h-5 w-5 mr-3" />
                      Setup Reddit Integration
                    </Button>
                  )}
                </div>

                {/* Benefits */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-zinc-900">You are about to unlock:</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      'Search and validate subreddits for your niche',
                      'Schedule posts for optimal engagement times',
                      'Autonomous AI agent for Reddit posting',
                      'Track post performance and analytics'
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-zinc-700">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Security Note */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-green-800">Secure & Private</p>
                      <p className="text-xs text-green-700 mt-1">
                        We use Reddit's official system. We never store your password and you can revoke access anytime.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    )
  }

  // If connected, render the children (the actual page content)
  return <>{children}</>
} 